import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  formatDate,
  useApiClient,
  useRequireOrganization,
} from '@startupquickstart/frontend';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCircle2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { WhatsNewDialog, type WhatsNewDoc } from './WhatsNewDialog';

/** How long the unseen-docs list is considered fresh. */
const WHATS_NEW_STALE_MS = 5 * 60 * 1000;
/** Session flag so the auto-surface only fires once per browser session. */
const AUTO_SHOWN_KEY = 'whats-new-auto-shown';
/** How many recent notes the popover keeps reachable (read or unread). */
const RECENT_NOTES_LIMIT = 10;

interface WhatsNewResponse {
  success: boolean;
  data: WhatsNewDoc[];
}

/**
 * Globally-visible "What's New" shortcut in the top-right of the layout.
 * Internal-only. Shows a dot when the current user has unseen published
 * release docs, opens a popover listing them, and lets the user read any doc
 * (which marks it viewed) or mark all as read at once.
 *
 * The newest unseen doc also auto-surfaces once per session so users don't
 * miss a release without clicking the bell.
 */
export function WhatsNewButton() {
  const { hasOrganization, isInternal } = useRequireOrganization();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<WhatsNewDoc | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);

  // Internal-only feature; hidden for personal accounts and non-internal orgs.
  const enabled = !!hasOrganization && !!isInternal;

  const { data } = useQuery<WhatsNewResponse>({
    queryKey: ['whats-new'],
    queryFn: () => apiClient.get('/release-notes/whats-new'),
    staleTime: WHATS_NEW_STALE_MS,
    enabled,
  });

  // Recent published notes, read or not — the popover keeps past
  // announcements reachable instead of only showing unseen ones.
  const { data: recentData } = useQuery<WhatsNewResponse>({
    queryKey: ['release-notes-recent'],
    queryFn: () =>
      apiClient.get(
        `/release-notes?limit=${RECENT_NOTES_LIMIT}&where[status][equals]=published&orderBy[publishedAt]=desc`,
      ),
    staleTime: WHATS_NEW_STALE_MS,
    enabled,
  });

  const docs = data?.data ?? [];
  const hasUnseen = docs.length > 0;
  const unseenIds = new Set(docs.map((doc) => doc.id));
  // Fall back to the unseen list if the recent query hasn't resolved yet, so
  // fresh announcements never wait on the second request.
  const recentNotes = recentData?.data?.length ? recentData.data : docs;

  // Auto-surface the newest unseen doc once per session.
  useEffect(() => {
    if (!hasUnseen) return;
    if (typeof window === 'undefined') return;
    if (window.sessionStorage.getItem(AUTO_SHOWN_KEY) !== null) return;
    window.sessionStorage.setItem(AUTO_SHOWN_KEY, '1');
    setSelectedDoc(docs[0]);
    setDialogOpen(true);
  }, [hasUnseen, docs]);

  if (!enabled) return null;

  const openDoc = (doc: WhatsNewDoc) => {
    setSelectedDoc(doc);
    setDialogOpen(true);
    setPopoverOpen(false);
  };

  const handleMarkAllRead = async () => {
    if (docs.length === 0) return;
    setMarkingAll(true);
    try {
      await Promise.all(
        docs.map((doc) => apiClient.post(`/release-notes/${doc.id}/view`, {})),
      );
      await queryClient.invalidateQueries({ queryKey: ['whats-new'] });
      await queryClient.invalidateQueries({ queryKey: ['release-notes-recent'] });
      setPopoverOpen(false);
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-tour="whats-new-bell"
            aria-label="What's new"
            title="What's new"
            className="relative inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <Bell className="h-4 w-4" />
            {hasUnseen && (
              <span
                data-testid="whats-new-dot"
                aria-hidden="true"
                className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-background"
              />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <h3 className="text-sm font-medium text-zinc-900">What's new</h3>
            {hasUnseen && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                loading={markingAll}
                onClick={handleMarkAllRead}
              >
                Mark all as read
              </Button>
            )}
          </div>
          {recentNotes.length > 0 ? (
            <>
              <ul className="max-h-96 divide-y overflow-y-auto">
                {recentNotes.map((doc) => {
                  const isUnseen = unseenIds.has(doc.id);
                  return (
                    <li key={doc.id}>
                      <button
                        type="button"
                        onClick={() => openDoc(doc)}
                        className="flex w-full items-start gap-2 px-4 py-3 text-left transition-colors hover:bg-muted/50 focus:outline-none focus:bg-muted/50"
                      >
                        <span
                          aria-hidden="true"
                          data-testid={isUnseen ? 'whats-new-item-unread' : undefined}
                          className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                            isUnseen ? 'bg-primary' : 'bg-transparent'
                          }`}
                        />
                        <span className="min-w-0 flex-1">
                          <span
                            className={`block text-sm ${
                              isUnseen
                                ? 'font-semibold text-zinc-900'
                                : 'font-medium text-zinc-700'
                            }`}
                          >
                            {doc.title}
                          </span>
                          {doc.summary && (
                            <span className="mt-0.5 line-clamp-2 block text-sm text-muted-foreground">
                              {doc.summary}
                            </span>
                          )}
                          {doc.publishedAt && (
                            <span className="mt-1 block text-xs text-muted-foreground">
                              {formatDate(doc.publishedAt)}
                            </span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div className="border-t px-4 py-2.5 text-center">
                <Link
                  to="/release-notes"
                  onClick={() => setPopoverOpen(false)}
                  className="text-sm font-medium text-primary hover:underline"
                >
                  View all release notes
                </Link>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center px-6 py-8 text-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                <CheckCircle2 className="h-5 w-5 text-muted-foreground" />
              </div>
              <p className="mt-3 text-sm font-medium text-zinc-900">
                You're all caught up
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                New features and updates will appear here when they ship.
              </p>
              <Link
                to="/release-notes"
                onClick={() => setPopoverOpen(false)}
                className="mt-4 text-sm font-medium text-primary hover:underline"
              >
                Browse past release notes
              </Link>
            </div>
          )}
        </PopoverContent>
      </Popover>

      <WhatsNewDialog doc={selectedDoc} open={dialogOpen} onOpenChange={setDialogOpen} />
    </>
  );
}
